import React, { ReactNode } from "react";
import { Col, Container, Row, Button } from "react-bootstrap";
import { BookHalf } from "react-bootstrap-icons";
import styled from "styled-components";

import { getOperationalAnalyticsWidget, createExportSchedule } from "../../../utils/Requests";
import MetricsTable, { ColumnGroup } from "../../../components/analytics/MetricsTable";
import POPIndicator from "../../../components/analytics/POPIndicator";
import { ExportButton, ScheduleExportModal, DocsSidebar, WidgetCard } from "../../../components/analytics";
import { ExportContext, ExportableData, ExportScheduleRequest } from "../../../services/export";
import { DISABLED_FILTER_CONFIG, AnalyticsFilters, DocsSidebarEntry } from "../../../types/analytics";
import { getWidgetTheme } from "../../../utils/analytics";

/**
 * Customer Overview page has no interactive filters.
 * Data is aggregated server-side over fixed time periods (7/30/60 days).
 * Filter config: DISABLED_FILTER_CONFIG
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const FILTER_CONFIG = DISABLED_FILTER_CONFIG;

const PageContainer = styled(Container)`
    margin-top: 20px;
`;

const HeaderRow = styled(Row)`
    margin-bottom: 20px;
`;

const LastUpdatedText = styled.p`
    font-size: 0.9em;
    color: #6c757d;
    margin-bottom: 5px;
`;

type CustomerOverviewProps = {
    standalone?: boolean;
    groups: string[];
    username: string;
    customerConfig: CustomerConfig;
};

type CustomerOverviewState = {
    data: Record<string, any>[];
    isLoading: boolean;
    error: string | null;
    dateGenerated: string | null;
    showScheduleModal: boolean;
    sidebarOpen: boolean;
    sidebarEntries: DocsSidebarEntry[];
};

// API response field names (snake_case from backend)
const API_FIELDS = {
    customer: "customer",
    dateGenerated: "date_generated",
    playsLast7days: "plays_last_7days",
    playsLast30days: "plays_last_30days",
    playsLast60days: "plays_last_60days",
    usersLast7days: "users_last_7days",
    usersLast30days: "users_last_30days",
    usersLast60days: "users_last_60days",
    viewersLast60days: "viewers_last_60days",
    viewsLast60days: "views_last_60days",
    brandsPlayedLast60days: "brands_played_last_60days",
    avgProgressLast60days: "avg_progress_last_60days",
    playsBounceRateLast60days: "plays_bounce_rate_last_60days"
} as const;

class CustomerOverview extends React.Component<CustomerOverviewProps, CustomerOverviewState> {
    private containerRef = React.createRef<HTMLDivElement>();

    constructor(props: CustomerOverviewProps) {
        super(props);
        
        this.state = {
            data: [],
            isLoading: true,
            error: null,
            dateGenerated: null,
            showScheduleModal: false,
            sidebarOpen: false,
            sidebarEntries: []
        };
    }

    componentDidMount(): void {
        this.fetchData();
    }

    private toggleSidebar = (): void => {
        this.setState(prevState => ({ sidebarOpen: !prevState.sidebarOpen }));
    };

    private fetchData = async (): Promise<void> => {
        try {
            this.setState({ isLoading: true, error: null });
            
            const response = await getOperationalAnalyticsWidget(
                "customer_overview"
            );
            
            // API returns { result: [...] }, extract the array
            const responseAny = response as any;
            const dataArray = responseAny?.result || (Array.isArray(response) ? response : []);

            if (dataArray && Array.isArray(dataArray) && dataArray.length > 0) {
                // Sort by customer name alphabetically
                const sortedData = dataArray.sort((a: Record<string, any>, b: Record<string, any>) =>
                    (a[API_FIELDS.customer] || "").localeCompare(b[API_FIELDS.customer] || "")
                );

                this.setState({
                    data: sortedData,
                    isLoading: false,
                    dateGenerated: sortedData.length > 0 ? sortedData[0][API_FIELDS.dateGenerated] : null
                });
            } else {
                this.setState({
                    data: [],
                    isLoading: false
                });
            }

            const meta = (response as any)?.widget;
            /* eslint-disable camelcase */
            const newEntry: DocsSidebarEntry | null = meta ? {
                widgetKey: "customer_overview",
                displayName: meta.display_name,
                description: meta.description,
                schema: (response as any)?.schema
            } : null;
            /* eslint-enable camelcase */
            const sidebarEntries: DocsSidebarEntry[] = [newEntry].filter((e): e is DocsSidebarEntry => e !== null);
            this.setState({ sidebarEntries });
        } catch (error) {
            this.setState({
                error: "Failed to load customer overview data",
                isLoading: false
            });
        }
    };

    private formatNumber = (value: number | null | undefined): string => {
        if (value == null) return "-";
        return value.toLocaleString();
    };

    private formatPercentage = (value: number | null | undefined): string => {
        if (value == null) return "-";
        return `${(value * 100).toFixed(1)}%`;
    };

    private formatProgress = (value: number | null | undefined): string => {
        if (value == null) return "-";
        return `${(value * 100).toFixed(0)}%`;
    };

    private convertToAnalyticsFilters = (): AnalyticsFilters => {
        const { dateGenerated } = this.state;
        const endDate = dateGenerated || new Date().toISOString().split("T")[0];
        const startDateObj = new Date(endDate);
        startDateObj.setDate(startDateObj.getDate() - 60);
        const startDate = startDateObj.toISOString().split("T")[0];
        return {
            startDate,
            endDate,
            attributionId: [],
            brandId: [],
            deviceType: []
        };
    };

    private getExportData = (): ExportableData[] => {
        const { data } = this.state;

        if (data.length === 0) {
            return [];
        }

        return [{
            title: "Customer Overview",
            columns: [
                { key: API_FIELDS.customer, label: "Customer" },
                { key: API_FIELDS.playsLast7days, label: "Plays (7d)", format: "number" },
                { key: API_FIELDS.playsLast30days, label: "Plays (30d)", format: "number" },
                { key: API_FIELDS.playsLast60days, label: "Plays (60d)", format: "number" },
                { key: API_FIELDS.usersLast7days, label: "Users (7d)", format: "number" },
                { key: API_FIELDS.usersLast30days, label: "Users (30d)", format: "number" },
                { key: API_FIELDS.usersLast60days, label: "Users (60d)", format: "number" },
                { key: API_FIELDS.viewersLast60days, label: "Viewers (60d)", format: "number" },
                { key: API_FIELDS.viewsLast60days, label: "Views (60d)", format: "number" },
                { key: API_FIELDS.brandsPlayedLast60days, label: "Brands (60d)", format: "number" },
                { key: API_FIELDS.avgProgressLast60days, label: "Avg Progress", format: "percent" },
                { key: API_FIELDS.playsBounceRateLast60days, label: "Bounce Rate", format: "percent" }
            ],
            rows: data,
            metadata: {
                filters: this.convertToAnalyticsFilters(),
                pageKey: "customer_overview",
                customerSlug: this.props.customerConfig.slug
            }
        }];
    };

    private getExportContext = (): ExportContext => {
        return {
            pageTitle: "Customer Overview",
            pageKey: "customer_overview",
            customerSlug: this.props.customerConfig.slug,
            filters: this.convertToAnalyticsFilters(),
            getData: this.getExportData,
            getContainerRef: () => this.containerRef.current
        };
    };

    private handleScheduleSave = async (schedule: ExportScheduleRequest & { id: string }): Promise<void> => {
        await createExportSchedule(this.props.customerConfig, schedule);
    };

    private getColumnGroups = (): ColumnGroup[] => {
        return [
            {
                name: "Customer",
                columns: [
                    {
                        key: API_FIELDS.customer,
                        header: "Name",
                        render: (value: string) => <strong>{value || "-"}</strong>
                    }
                ]
            },
            {
                name: "Plays Metrics",
                columns: [
                    {
                        key: API_FIELDS.playsLast7days,
                        header: "7 Days",
                        render: (value: number | null, row: Record<string, any>) => (
                            <POPIndicator
                                currentValue={value}
                                previousValue={row[API_FIELDS.playsLast30days] ? row[API_FIELDS.playsLast30days] / 4 : null}
                                formatValue={this.formatNumber}
                                showPercentageChange={false}
                            />
                        )
                    },
                    {
                        key: API_FIELDS.playsLast30days,
                        header: "30 Days",
                        render: (value: number | null, row: Record<string, any>) => (
                            <POPIndicator
                                currentValue={value}
                                previousValue={row[API_FIELDS.playsLast60days] ? row[API_FIELDS.playsLast60days] / 2 : null}
                                formatValue={this.formatNumber}
                            />
                        )
                    },
                    {
                        key: API_FIELDS.playsLast60days,
                        header: "60 Days",
                        render: (value: number | null) => this.formatNumber(value)
                    }
                ]
            },
            {
                name: "Users Metrics",
                columns: [
                    {
                        key: API_FIELDS.usersLast7days,
                        header: "7 Days",
                        render: (value: number | null) => this.formatNumber(value)
                    },
                    {
                        key: API_FIELDS.usersLast30days,
                        header: "30 Days",
                        render: (value: number | null, row: Record<string, any>) => (
                            <POPIndicator
                                currentValue={value}
                                previousValue={row[API_FIELDS.usersLast60days] ? row[API_FIELDS.usersLast60days] / 2 : null}
                                formatValue={this.formatNumber}
                            />
                        )
                    },
                    {
                        key: API_FIELDS.usersLast60days,
                        header: "60 Days",
                        render: (value: number | null) => this.formatNumber(value)
                    }
                ]
            },
            {
                name: "Other Metrics",
                columns: [
                    {
                        key: API_FIELDS.viewersLast60days,
                        header: "Viewers (60d)",
                        render: (value: number | null) => this.formatNumber(value)
                    },
                    {
                        key: API_FIELDS.viewsLast60days,
                        header: "Views (60d)",
                        render: (value: number | null) => this.formatNumber(value)
                    },
                    {
                        key: API_FIELDS.brandsPlayedLast60days,
                        header: "Brands (60d)",
                        render: (value: number | null) => this.formatNumber(value)
                    },
                    {
                        key: API_FIELDS.avgProgressLast60days,
                        header: "Avg Progress",
                        render: (value: number | null) => this.formatProgress(value)
                    },
                    {
                        key: API_FIELDS.playsBounceRateLast60days,
                        header: "Bounce Rate",
                        render: (value: number | null) => (
                            <POPIndicator
                                currentValue={value}
                                previousValue={null}
                                formatValue={this.formatPercentage}
                                showPercentageChange={false}
                                invertColors={true}
                            />
                        )
                    }
                ]
            }
        ];
    };

    public render(): ReactNode {
        const { data, isLoading, error, dateGenerated, sidebarOpen, sidebarEntries } = this.state;
        const columnGroups = this.getColumnGroups();

        return (
            <div ref={this.containerRef} style={{ display: "flex", alignItems: "flex-start" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
            <PageContainer className="mw-100" style={{ position: "relative" }}>
                <div data-export-ignore style={{ position: "absolute", right: "0px", top: "0px", zIndex: 1000, display: "flex", gap: "8px", alignItems: "center" }}>
                    <ExportButton
                        context={this.getExportContext()}
                        enabled={!isLoading && !error && data.length > 0}
                        scheduledEnabled={this.props.customerConfig.exportConfig?.scheduledEnabled !== false}
                        onScheduleClick={() => this.setState({ showScheduleModal: true })}
                    />
                    <Button
                        onClick={this.toggleSidebar}
                        style={{ backgroundColor: "transparent", border: "none", fontSize: "20px", padding: "4px 8px" }}
                        title={sidebarOpen ? "Close Documentation" : "Open Documentation"}
                    >
                        <BookHalf />
                    </Button>
                </div>

                <HeaderRow>
                    <Col md style={{ textAlign: "center" }}>
                        <h2 style={{ margin: 0 }}>Customer Overview</h2>
                        {dateGenerated && (
                            <LastUpdatedText>
                                Data generated: {new Date(dateGenerated).toLocaleDateString()}
                            </LastUpdatedText>
                        )}
                    </Col>
                </HeaderRow>

                <WidgetCard
                    title="Customer Overview"
                    description={sidebarEntries.find(e => e.widgetKey === "customer_overview")?.description}
                    isLoading={isLoading}
                    error={error}
                    isEmpty={!isLoading && !error && data.length === 0}
                    emptyMessage="No customer data available"
                    theme={getWidgetTheme(true)}
                >
                    <MetricsTable
                        data={data}
                        columnGroups={columnGroups}
                        rowKeyField={API_FIELDS.customer}
                        emptyMessage="No customer data available"
                    />
                </WidgetCard>

                <ScheduleExportModal
                    show={this.state.showScheduleModal}
                    onHide={() => this.setState({ showScheduleModal: false })}
                    filters={this.convertToAnalyticsFilters()}
                    pageKey="customer_overview"
                    pageTitle="Customer Overview"
                    customerSlug={this.props.customerConfig.slug}
                    editingSchedule={null}
                    onSave={this.handleScheduleSave}
                />
            </PageContainer>
            </div>
            <DocsSidebar isOpen={sidebarOpen} entries={sidebarEntries} />
            </div>
        );
    }
}

export default CustomerOverview;
